import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { useContent } from "@/context/ContentContext";

const DEFAULT_SECTIONS = [
  { title: "1. Acceptance of Terms", body: "By accessing or using 786i.net (the \"Site\") or engaging 786 Interactive (\"we\", \"us\", \"our\") for services, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, please do not use the Site or our services." },
  { title: "2. Services", body: "786 Interactive provides web and mobile application design and development services. The scope, deliverables, timeline and fees for any engagement are defined in a separate written proposal or agreement between us and the client. These Terms apply in addition to any such agreement." },
  { title: "3. Quotes & Payments", body: "Quotes provided through the Site or by email are estimates and remain valid for 30 days unless stated otherwise. Unless agreed in writing, invoices are payable within 14 days. Work may be paused on accounts with overdue balances." },
  { title: "4. Intellectual Property", body: "Upon full payment, clients receive ownership of the bespoke deliverables created for their project, unless otherwise agreed. We retain ownership of our pre-existing tools, frameworks and know-how, and grant the client a perpetual licence to use them within the delivered work." },
  { title: "5. Client Responsibilities", body: "Clients are responsible for providing timely feedback, content and access required to deliver the project, and for ensuring that any materials supplied to us do not infringe third-party rights." },
  { title: "6. Warranties & Liability", body: "We build with care and test thoroughly, but software is provided \"as is\" beyond any agreed warranty period. To the maximum extent permitted by law, our total liability is limited to the fees paid for that engagement." },
  { title: "7. Privacy", body: "Information submitted through our contact form is used solely to respond to your inquiry and is never sold or shared with third parties for marketing purposes." },
  { title: "8. Changes to These Terms", body: "We may update these Terms from time to time. The latest version will always be available on this page, with the effective date shown below." },
  { title: "9. Contact", body: "Questions about these Terms? Reach us through the contact form at 786i.net." },
];

export default function Terms() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { content } = useContent();
  const t = content?.terms;
  const sections = t?.sections?.length ? t.sections : DEFAULT_SECTIONS;

  return (
    <>
      <Helmet>
        <title>Terms &amp; Conditions | 786 Interactive — 786i.net</title>
        <meta
          name="description"
          content="Read the Terms and Conditions for 786 Interactive (786i.net) — covering services, intellectual property, payments, privacy and client responsibilities."
        />
        <link rel="canonical" href={`${window.location.origin}/terms`} />
        <meta property="og:title" content="Terms &amp; Conditions | 786 Interactive" />
        <meta property="og:url" content={`${window.location.origin}/terms`} />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <main data-testid="terms-page" className="bg-[#F7F6F1] text-[#122A1D] min-h-screen">
      <Header />

      <section className="pt-36 pb-24 px-6 lg:px-12 max-w-4xl mx-auto">
        <p className="uppercase tracking-[0.35em] text-xs text-[#8C6A1F] mb-6 flex items-center gap-4">
          <span className="w-12 h-px bg-[#A9842F]/70" />
          Legal
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight">
          {t?.heading || "Terms &"} <em className="text-[#A9842F]">{t?.heading_accent || "Conditions"}</em>
        </h1>
        <p className="mt-4 text-sm text-[#5B6F64]">Effective date: {t?.effective_date || "June 12, 2026"}</p>

        <div className="mt-12 space-y-10">
          {sections.map((s, i) => (
            <div key={i} data-testid={`terms-section-${s.title.split(".")[0]}`}>
              <h2 className="font-heading text-2xl text-[#122A1D] mb-3">{s.title}</h2>
              <p className="text-[#44584C] font-light leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </main>
    </>
  );
}
